<template>
    <div :class="[ 'connectwise-send-widget' ]">
        <div class="widget-header">
            <h3>Send To ConnectWise</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div v-if="isInitializing">
            <loading-screen-svg />
        </div>
        <div v-else class="content-wrapper">
            <connectwise-send-contact v-if="contact" @selectCompany="selectCompany" @removeCompany="removeCompany" :contact="contact" :company="company"/>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" :onClick="closeWidget">
                     Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button :onClick="sendContactToConnectWise" :loading="isSendingContactToConnectWise" :disabled="isSendingContactToConnectWise || deactivatedSwitches">
                    Send to ConnectWise
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const ConnectWiseSendContact = () => import('@/components/integration/connectwise-send-contact');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'connectwise-send-contact-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'connectwise-send-contact': ConnectWiseSendContact
    },

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            contact: {
                id: null,
                name: null,
                profileImageUrl: null,
                shouldSendInformation: true,
                shouldSendOpportunities: false,
                shouldSendActivities: false,
                errorMessage: null,
                isUpsert: null,
                clientId: null,
                connectWiseContactId: null,
            },
            company: {
                id: null,
                name: null,
                imageUrl: null,
                connectWiseCompanyId: -1,
            },
            isSavingCompanyToContact: false,
            isSendingContactToConnectWise: false,
            deactivatedSwitches: false,
            cwContactLinkData: {
                connectWiseSite: null,
                connectWiseCompany: null,
                connectWiseContactId: null
            },
            isInitializing: true
        }
    },

    computed: {
        connectWiseLink: function() {
            return `https://${ this.cwContactLinkData.connectWiseSite }/v4_6_release/services/system_io/router/openrecord.rails?locale=en_US&recordType=ContactFv&recid=${ this.cwContactLinkData.connectWiseContactId }&companyName=${ this.cwContactLinkData.connectWiseCompany }`;
        }
    },

    watch: {
        'contact.shouldSendInformation': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        },

        'contact.shouldSendOpportunities': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        },

        'contact.shouldSendActivities': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        }
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getData() {
            this.getContact()
            .then(this.getIntegrationInformation)
            .catch((error) => {
                this.$helpers.showFailNotification(error, { error });
            });
        },

        getContact() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Contacts?$filter=Id eq ${ this.widget.options.id }&$expand=Company`)
                    .then(response => {
                        this.createContact(response.data.value[0]);
    
                        if (response.data.value[0].Company) {
                            this.createCompany(response.data.value[0].Company);
                        }
    
                        resolve();
                    })
                    .catch((error) => {
                        reject('An error occurred while fetching the contacts you selected.');
                });
            });
        },

        getIntegrationInformation() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/ms/cw-api/v1/credentials/${ this.contact.clientId }`)
                    .then(response => {
                        this.cwContactLinkData.connectWiseSite = response.data.Url;
                        this.cwContactLinkData.connectWiseCompany = response.data.CompanyId;
                    })
                    .catch((error) => {
                        reject('An error occurred while fetching your ConnectWise integration info.');
                    })
                    .finally(()=> {
                            this.isInitializing = false;
                    });
            });
        },

        createContact(contactsResponse) {
            this.contact.id = contactsResponse.Id;
            this.contact.name = this.$helpers.getName(contactsResponse.FirstName, contactsResponse.LastName);
            this.contact.profileImageUrl = contactsResponse.ProfileImageUrl || require('@/assets/images/avatar.png');
            this.contact.shouldSendInformation = true;
            this.contact.shouldSendOpportunities = false;
            this.contact.shouldSendActivities = false;
            this.contact.errorMessage = null;
            this.contact.selectedOption = contactsResponse.IsUpsert ? 'upsert' : 'insert';
            this.contact.clientId = contactsResponse.ClientId;
        },

        createCompany(company) {
            this.company.id = company.Id;
            this.company.name = company.Name;
            this.company.imageUrl = company.LogoUrl || require('@/assets/images/company-avatar.png');
            this.company.connectWiseCompanyId = company.ConnectWiseCompanyId;
        },

        selectCompany(company) {
            this.company = {
                id: company.id,
                name: company.name,
                imageUrl: company.imageUrl
            }

            this.saveCompanyToContact();
        },

        removeCompany() {
            this.company = null;
        },

        openCreateCompanyWidget() {
            var widget = {
                name: 'quick-add-company-widget'
            };

            EventBus.$emit('open-widget', widget);
        },

        saveCompanyToContact() {
            this.isSavingCompanyToContact = true;

            var changes = [{
                'name': 'CompanyId',
                'value': this.company.id
            }];

            this.$api
                .patch(`/api/Contacts/${ this.widget.options.id }`, changes)
                .then(response => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showSuccessNotification(`<strong>${ this.contact.name }</strong> has been added to ${ this.company.name }`);
                })
                .catch((error) => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showFailNotification('An error occurred while adding your contact to a company.', { error });
                });
        },

        sendContactToConnectWise() {
            this.isSendingContactToConnectWise = true;

            this.sendCompanyInfo()
            .then(this.sendContactInfo)
            .then(this.getConnectWiseContactId)
            .then(() => {
                this.isSendingContactToConnectWise = false;
                this.contact.errorMessage = null;

                this.createSuccessMessage();

                EventBus.$emit('contact:update');
                this.closeWidget();
            })
            .catch(error => {
                this.isSendingContactToConnectWise = false;
                this.contact.errorMessage = error.Message;
            });
        },

        getConnectWiseContactId() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Contacts?$filter=Id eq ${ this.contact.id } &$select=ConnectWiseContactId`)
                    .then(response => {
                        this.cwContactLinkData.connectWiseContactId = response.data.value[0].ConnectWiseContactId;
                        resolve();
                    })
                    .catch((response) => {
                        reject(response.response.data);
                });
            });
        },

        createSuccessMessage() {
            var actionTaken = this.contact.isUpsert ? 'updated' : 'inserted';
            var message = `<strong>${ this.contact.name }</strong> has been ${ actionTaken } in ConnectWise.`;

            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link: this.connectWiseLink,
                action: null
            });
        },

        sendCompanyInfo() {
            return new Promise((resolve, reject) => {
                if(this.company && !this.company.connectWiseCompanyId) {
                    var request = {
                       isUpsert: false 
                    };

                    this.$api
                        .post(`/ms/cw-api/v1/integrations/export/companies/${ this.company.id }/${ this.contact.clientId }`, request)
                        .then(response => {
                            this.$helpers.showSuccessNotification(`${ this.company.name } has been inserted in ConnectWise`)
                            resolve();
                        })
                        .catch((response) => {
                            reject(response.response.data);
                        });
                }
                else {
                    resolve();
                }
            });
        },
        
        sendContactInfo() {
            return new Promise((resolve, reject) => {
                var request = {
                    isUpsert: this.contact.isUpsert,
                    exportContacts: this.contact.shouldSendInformation,
                    exportDeals: this.contact.shouldSendOpportunities,
                    exportActivities: this.contact.shouldSendActivities
                };

                this.$api
                    .post(`/ms/cw-api/v1/integrations/export/contacts/${ this.contact.id }/${ this.contact.clientId }`, request)
                    .then(response => {
                        resolve();
                    })
                    .catch((response) => {
                        reject(response.response.data);
                });
            })
        }
    },

    created() {
        this.getData();
    }
}
</script>

<style scoped>
.connectwise-send-widget .content-wrapper {
    padding-left: 0px;
    padding-right: 0px;
}

.connectwise-send-widget .btn.transparent-btn {
    background: transparent;
    color: #877D88;
}

.connectwise-send-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18AEFF;
}

.connectwise-send-widget .action-container .btn.transparent-btn:hover {
    background: #DBEDFA;
}

.connectwise-send-widget .btn.transparent-btn:hover {
    background: #E1E0E5;
}

.connectwise-send-widget .company-options-container h4 {
    margin-bottom: 20px;
}

.connectwise-send-widget .widget-content .processing {
    display: block;
    width: 200px;
    margin: 0 auto;
}

.connectwise-send-widget .widget-content .row {
    margin-bottom: 30px;
}
</style>